/** Request bodies. Frontends use these for form validation; workers use them to reject bad input. */
import { z } from 'zod';
import { LANGS } from './i18n.ts';
import { CONSENT_CHANNELS, HOME_SECTIONS, SITE_TEMPLATES } from './contract.ts';
import { ADMIN_ROLES } from './growth.ts';
import { posItemCodes, posSettingsSchema } from './pos.ts';

export const langSchema = z.enum(LANGS);
export const localizedTextSchema = z.object({
  'zh-Hant': z.string().max(20000).optional(),
  'zh-Hans': z.string().max(20000).optional(),
  en: z.string().max(20000).optional(),
});

const email = z.string().trim().toLowerCase().email().max(254);
/** At least 8 characters with a letter and a digit. */
export const passwordSchema = z
  .string()
  .min(8)
  .max(128)
  .regex(/[A-Za-z]/, 'needs a letter')
  .regex(/\d/, 'needs a digit');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const optionalText = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));
const url = z.string().trim().url().max(500);
/**
 * Brand member sites live at <slug>.loyaltyplus.vip (and their old <slug>.loyaltyplus.hk) next to the platform's own hostnames, so these
 * can never be brand slugs.
 */
export const RESERVED_SLUGS = [
  'www', 'api', 'adminapi', 'adminportal', 'admin', 'brand', 'brands', 'portal', 'member', 'members', 'app', 'apps',
  'mail', 'webmail', 'smtp', 'imap', 'pop', 'mx', 'ns1', 'ns2', 'autodiscover', 'ftp', 'cdn', 'static', 'assets', 'media',
  'status', 'help', 'support', 'docs', 'blog', 'dev', 'staging', 'test', 'uat', 'demo', 'sso', 'auth', 'login', 'posapi',
] as const;

const slug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, 'lowercase letters, digits and dashes')
  .refine((s) => !(RESERVED_SLUGS as readonly string[]).includes(s), 'reserved for the platform');
const hostname = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'a domain like member.brand.com');

// ---------------------------------------------------------------- member site

/** Per-channel marketing consent; a missing channel means "not asked / unchanged". */
export const consentsInputSchema = z.object({
  email: z.boolean().optional(),
  sms: z.boolean().optional(),
  whatsapp: z.boolean().optional(),
  wallet: z.boolean().optional(),
});
export type ConsentsInput = z.infer<typeof consentsInputSchema>;

export const registerSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  email,
  /** Optional only when the phone number is proven with `smsCode` (the member then logs in by SMS). */
  password: passwordSchema.optional(),
  phoneCc: z.string().regex(/^\d{1,4}$/).optional().or(z.literal('')),
  phone: z.string().regex(/^\d{6,15}$/).optional().or(z.literal('')),
  birthday: isoDate.optional().or(z.literal('')),
  gender: z.enum(['M', 'F']).optional().or(z.literal('')),
  area: optionalText(60),
  acceptTerms: z.literal(true),
  /** Legacy single opt-in; used for email when `consents` is absent. */
  marketingOptIn: z.boolean(),
  consents: consentsInputSchema.optional(),
  lang: langSchema,
  /** A friend's referral code (typed, or filled in from their link). A 渠道追蹤 link code is also accepted. */
  referralCode: z.string().trim().regex(/^[A-Za-z0-9_-]{1,40}$/).optional(),
  turnstileToken: z.string().max(4096).optional(),
  /** Code from POST /api/member/auth/sms/send (purpose register) for phoneCc + phone. */
  smsCode: z.string().regex(/^\d{6}$/).optional(),
  /** /register?google=token: sign up with the Google account checked there (email fixed, no password needed). */
  googleToken: z.string().regex(/^[A-Za-z0-9_-]{20,64}$/).optional(),
  /** The code emailed to `email` when this call first answered verify_email (LPSS-42). */
  emailCode: z.string().regex(/^\d{6}$/).optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const verifyExistingSchema = z.object({
  email,
  code: z.string().regex(/^\d{6}$/),
  /** Same fields as the first register call; the server needs the password again to set it in the POS. */
  registration: registerSchema,
});
export type VerifyExistingInput = z.infer<typeof verifyExistingSchema>;

export const loginSchema = z.object({
  email,
  password: z.string().min(1).max(128),
  turnstileToken: z.string().max(4096).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const profileSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  phoneCc: z.string().regex(/^\d{1,4}$/).optional().or(z.literal('')),
  phone: z.string().regex(/^\d{6,15}$/).optional().or(z.literal('')),
  birthday: isoDate.optional().or(z.literal('')),
  gender: z.enum(['M', 'F']).optional().or(z.literal('')),
  area: optionalText(60),
  marketingOptIn: z.boolean(),
  consents: consentsInputSchema.optional(),
  lang: langSchema,
});
export type ProfileInput = z.infer<typeof profileSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});

export const acceptTermsSchema = z.object({ version: z.string().trim().min(1).max(40) });

export const forgotPasswordSchema = z.object({ email, turnstileToken: z.string().max(4096).optional() });

export const resetPasswordSchema = z.object({
  email,
  code: z.string().regex(/^\d{6}$/),
  newPassword: passwordSchema,
});

export const DELETION_REASONS = ['no_longer_use', 'too_many_messages', 'privacy', 'moving_away', 'other'] as const;
export const deletionRequestSchema = z.object({
  reason: z.enum(DELETION_REASONS),
  reasonText: optionalText(500),
});

// ---------------------------------------------------------------- staff auth

export const staffLoginSchema = z.object({
  email,
  password: z.string().min(1).max(128),
  otp: z.string().regex(/^\d{6}$/).optional(),
});

export const acceptInviteSchema = z.object({
  token: z.string().min(20).max(200),
  name: z.string().trim().min(1).max(80),
  password: passwordSchema.min(10),
});

export const bootstrapAdminSchema = z.object({
  token: z.string().min(16).max(200),
  email,
  name: z.string().trim().min(1).max(80),
  password: passwordSchema.min(10),
});

export const totpEnableSchema = z.object({ secret: z.string().min(16).max(64), otp: z.string().regex(/^\d{6}$/) });

// ---------------------------------------------------------------- brand portal

/** An account name ("mtrhk", "@mtrhk") or a full https:// link; see socialUrl(). */
const socialHandle = z
  .string()
  .trim()
  .max(300)
  .refine((v) => v === '' || /^https?:\/\/\S+$/i.test(v) || /^@?[\w.\-/]+$/.test(v), 'an account name or a link');
const socials = z.object({
  facebook: socialHandle.optional(),
  instagram: socialHandle.optional(),
  whatsapp: z.string().trim().max(40).optional(),
  youtube: socialHandle.optional(),
  threads: socialHandle.optional(),
  xiaohongshu: socialHandle.optional(),
  linkedin: socialHandle.optional(),
  website: z.string().trim().max(500).optional(),
});

const fieldRule = z.enum(['hidden', 'optional', 'required']);
/** A Google Maps share link (maps.app.goo.gl/…, goo.gl/maps/…, google.com/maps/…). */
const mapUrl = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || /^https:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps|(www\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+)\S*$/i.test(v), 'a Google Maps link')
  .optional();
const monthDay = z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'MM-DD');
const imageKey = z.string().max(300);

// ---- automations (settings section) and reminders

const automationRule = {
  enabled: z.boolean(),
  email: z.boolean(),
  title: localizedTextSchema,
  body: localizedTextSchema,
  rewardId: z.string().min(1).max(64).nullable(),
  points: z.number().int().min(0).max(1_000_000),
};
const automationSettingsSchema = z.object({
  pointsExpiry: z.object({ ...automationRule, daysBefore: z.array(z.number().int().min(1).max(365)).min(1).max(5) }),
  inactive: z.object({ ...automationRule, days: z.number().int().min(7).max(3650) }),
  tierUpgrade: z.object(automationRule),
  tierExpiry: z.object({ ...automationRule, daysBefore: z.number().int().min(1).max(365) }),
  couponExpiry: z.object({ ...automationRule, daysBefore: z.number().int().min(1).max(365) }),
});

/** "$amount = stamps" (印花比例). */
export const stampRatioSchema = z.object({ amount: z.number().positive().max(1_000_000), stamps: z.number().int().min(1).max(1000) });

// ---- referrals (settings section)

const referralGift = z.object({ points: z.number().int().min(0).max(1_000_000), rewardId: z.string().min(1).max(64).nullable() });
const referralSettingsSchema = z.object({
  enabled: z.boolean(),
  trigger: z.enum(['register', 'first_purchase']),
  referrer: referralGift,
  referee: referralGift,
  monthlyCap: z.number().int().min(1).max(10000).nullable(),
  terms: localizedTextSchema,
});


const storedValueSettingsSchema = z.object({
  bonusRules: z.array(z.object({ topup: z.number().positive().max(1_000_000), bonus: z.number().min(0).max(1_000_000), repeat: z.boolean() })).max(10),
  minTopup: z.number().min(0).max(1_000_000),
  maxBalance: z.number().positive().max(10_000_000).nullable(),
  validDays: z.number().int().min(0).max(3650).nullable(),
  skus: z.array(z.object({ sku: z.string().trim().min(1).max(40), amount: z.number().positive().max(1_000_000) })).max(50),
  terms: localizedTextSchema,
});

export const settingsSectionSchemas = {
  profile: z.object({
    name: localizedTextSchema,
    about: localizedTextSchema,
    address: localizedTextSchema,
    mapUrl,
    officeHours: localizedTextSchema,
    phone: z.string().trim().max(40),
    email: z.string().trim().max(254),
    socials,
    stores: z
      .array(
        z.object({
          /** Given by the server on save when missing; keep it when editing (sales and staff refer to it). */
          id: z.string().max(64).nullish(),
          name: localizedTextSchema,
          address: localizedTextSchema,
          phone: z.string().trim().max(40).optional(),
          hours: localizedTextSchema,
          mapUrl,
          /** shopCode the POS sends for this shop (POS 連接). */
          posCode: z.string().trim().max(32).optional(),
          /** Where the shop is, for Wallet 附近通知. */
          location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).nullable().optional(),
        }),
      )
      .max(50),
    galleryKeys: z.array(imageKey).max(10).optional(),
  }),
  theme: z.object({
    template: z.enum(SITE_TEMPLATES),
    colors: z.object({ primary: hexColor, secondary: hexColor, accent: hexColor }),
    logoKey: imageKey.nullable(),
    faviconKey: imageKey.nullable(),
    storyImageKey: imageKey.nullable().optional(),
  }),
  languages: z
    .object({ enabled: z.array(langSchema).min(1), default: langSchema })
    .refine((v) => v.enabled.includes(v.default), { message: 'default must be enabled', path: ['default'] }),
  home: z.object({
    sections: z.array(z.object({ key: z.enum(HOME_SECTIONS), on: z.boolean() })).max(HOME_SECTIONS.length).nullable(),
    bannerSeconds: z.number().int().min(3).max(30),
  }),
  seo: z.object({
    title: localizedTextSchema,
    description: localizedTextSchema,
    ogImageKey: z.string().max(300).nullable(),
  }),
  email: z.object({
    senderName: z.string().trim().min(1).max(80),
    replyTo: z.string().trim().max(254),
    welcomeSubject: localizedTextSchema,
    welcomeHeader: localizedTextSchema,
    welcomeBody: localizedTextSchema,
    welcomeFooter: localizedTextSchema,
  }),
  registration: z.object({
    gender: fieldRule,
    phone: fieldRule,
    birthday: fieldRule,
    area: fieldRule,
    areaOptions: z.array(z.object({ value: z.string().trim().min(1).max(60), label: localizedTextSchema })).max(100),
    /** Members may sign up / log in with a code sent by SMS (needs the smsLogin module and the platform SMS gateway). */
    phoneLogin: z.boolean().default(false),
    /** Members may sign up / log in with their Google account (needs the platform's 「Google 登入」). */
    googleLogin: z.boolean().default(true),
  }),
  legal: z.object({ terms: localizedTextSchema, privacy: localizedTextSchema, termsVersion: z.string().trim().min(1).max(40) }),
  features: z.object({ news: z.boolean(), rewards: z.boolean(), wallet: z.boolean().default(false) }),
  consent: z.object({ channels: z.array(z.enum(CONSENT_CHANNELS)).max(4), statement: localizedTextSchema }),
  program: z.object({
    yearStart: monthDay,
    yearEnd: monthDay,
    pointsClearDate: monthDay.nullable(),
    // An older client that leaves out the ratio saves "no ratio".
    stamps: z.object({ enabled: z.boolean(), clearDate: monthDay.nullable(), ratio: stampRatioSchema.nullable().default(null) }),
    welcome: z.object({ enabled: z.boolean(), rewardId: z.string().max(64).nullable(), points: z.number().int().min(0).max(1_000_000) }),
    birthday: z.object({ enabled: z.boolean(), rewardId: z.string().max(64).nullable(), points: z.number().int().min(0).max(1_000_000), timing: z.enum(['day', 'month']) }),
  }),
  automations: automationSettingsSchema,
  referral: referralSettingsSchema,
  storedValue: storedValueSettingsSchema,
  tracking: z.object({ windowHours: z.number().int().min(1).max(2160) }),
  pos: posSettingsSchema,
  memberNo: z.object({
    prefix: z.string().trim().toUpperCase().regex(/^[A-Z]{0,6}$/),
    next: z.number().int().min(1).max(999_999_999),
  }),
} as const;

/** Unsaved settings to look at on the member site before saving (POST /api/portal/settings/preview). */
export const settingsPreviewSchema = z.object({
  profile: settingsSectionSchemas.profile.optional(),
  theme: settingsSectionSchemas.theme.optional(),
  home: settingsSectionSchemas.home.optional(),
});

/** A wallet notification to every card holder who allows it. Title and body appear on the lock screen. */
/** Google Wallet: at most 3 notifications per card in 24 hours; the platform holds Apple cards to the same. */
export const WALLET_PER_CARD_DAILY = 3;
/** Furthest ahead a Wallet message can be scheduled. */
export const WALLET_SCHEDULE_MAX_DAYS = 90;

export const walletMessageSchema = z.object({
  title: localizedTextSchema,
  body: localizedTextSchema,
  /** Only members of this segment (still only those who allow wallet notifications); absent = everyone. */
  segmentId: z.string().min(1).max(64).nullable().optional(),
  /** When to send; null or absent = now. The segment is read when it goes out. */
  sendAt: z.string().datetime({ offset: true }).nullable().optional(),
});

/** Apple Wallet takes at most 10 locations on a card, Google Wallet 10 on a card's class. */
export const WALLET_NEARBY_MAX_SHOPS = 10;
/** Apple shows relevantText on one short line of the lock screen. */
export const WALLET_NEARBY_TEXT_SOFT = 40;

/** 附近通知: for a period, cards carry these shops' locations (and, on iPhone, the text shown near them). */
export const walletNearbySchema = z
  .object({
    text: localizedTextSchema,
    shopIds: z.array(z.string().min(1).max(64)).min(1).max(WALLET_NEARBY_MAX_SHOPS),
    startAt: z.string().datetime({ offset: true }),
    /** null = until switched off. */
    endAt: z.string().datetime({ offset: true }).nullable(),
    active: z.boolean(),
  })
  .refine((v) => v.endAt === null || v.endAt > v.startAt, { message: 'end before start', path: ['endAt'] });
export type WalletNearbyInput = z.infer<typeof walletNearbySchema>;

export const bannerSchema = z.object({
  imageKey: z.string().min(1).max(300),
  mobileImageKey: z.string().max(300).nullable(),
  title: localizedTextSchema,
  link: z.string().trim().max(500).nullable(),
  startAt: z.string().datetime({ offset: true }).nullable(),
  endAt: z.string().datetime({ offset: true }).nullable(),
  active: z.boolean(),
});

export const reorderSchema = z.object({ ids: z.array(z.string().max(64)).max(500) });

export const newsCategorySchema = z.object({ name: localizedTextSchema, sort: z.number().int().min(0).max(100000) });

export const newsSchema = z.object({
  categoryId: z.string().max(64).nullable(),
  title: localizedTextSchema,
  summary: localizedTextSchema,
  body: localizedTextSchema,
  imageKey: z.string().max(300).nullable(),
  publishStart: z.string().datetime({ offset: true }).nullable(),
  publishEnd: z.string().datetime({ offset: true }).nullable(),
  featured: z.boolean(),
  active: z.boolean(),
});

export const levelDisplaySchema = z.object({
  name: localizedTextSchema,
  color: hexColor,
  benefits: localizedTextSchema,
  sort: z.number().int().min(0).max(1000),
  cardImageKey: imageKey.nullable().optional(),
  iconKey: imageKey.nullable().optional(),
});

const money = z.number().min(0).max(100_000_000);
/** Native brands: a level of the programme. */
export const tierSchema = z.object({
  name: localizedTextSchema,
  description: localizedTextSchema,
  color: hexColor,
  cardImageKey: imageKey.nullable(),
  iconKey: imageKey.nullable(),
  sort: z.number().int().min(0).max(1000),
  isDefault: z.boolean(),
  upgradeSpend: money.nullable(),
  retainSpend: money.nullable(),
  validityMonths: z.number().int().min(1).max(120).nullable(),
  upgradeToId: z.number().int().positive().nullable(),
  downgradeToId: z.number().int().positive().nullable(),
  earn: z.object({ amount: z.number().positive().max(1_000_000), points: z.number().min(0).max(1_000_000) }),
  /** Ignored: the stamp ratio is the same for every level (program.stamps.ratio, LPSS-21). Accepted for older clients. */
  stamps: stampRatioSchema.nullable().optional(),
});
export type TierInput = z.infer<typeof tierSchema>;

/** One reward on the stamp card; `id` keeps an existing one (and its 已送出 count); null = a new one. */
export const stampRewardSchema = z
  .object({
    id: z.string().max(64).nullish(),
    stamps: z.number().int().min(1).max(10_000),
    title: localizedTextSchema,
    rewardId: z.string().max(64).nullable(),
    points: z.number().int().min(0).max(1_000_000),
  })
  .refine((v) => v.rewardId || v.points > 0, { message: 'give a coupon or points', path: ['rewardId'] });

/** Most rewards along the way on one card. */
export const STAMP_STEPS_MAX = 30;

/**
 * LPSS-33: the whole stamp card, saved at once: the final reward (reaching it completes the card) and the rewards
 * along the way, each at fewer stamps than the final one. LPSS-35: validTo (有效日期) = the last day the card works,
 * null = it does not expire.
 */
export const stampCardSchema = z
  .object({ final: stampRewardSchema, steps: z.array(stampRewardSchema).max(STAMP_STEPS_MAX), validTo: isoDate.nullable().default(null) })
  .superRefine((v, ctx) => {
    v.steps.forEach((s, i) => {
      if (s.stamps >= v.final.stamps) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', i, 'stamps'], message: `must be lower than the final reward (${v.final.stamps})` });
    });
  });
export type StampCardInput = z.infer<typeof stampCardSchema>;

/** One product of a sale (SaleLine). */
export const saleLineSchema = z.object({
  sku: z.string().trim().min(1).max(40),
  quantity: z.number().int().min(1).max(9999),
  price: z.number().min(0).max(10_000_000).nullable(),
});

/** Native brands: a sale recorded at the counter. */
export const purchaseSchema = z.object({
  amount: z.number().positive().max(10_000_000),
  invoiceNo: optionalText(60),
  /** Shop name as typed (older clients); prefer shopId. */
  shop: optionalText(120),
  /** StoreInfo.id; staff limited to some shops must give one of theirs. */
  shopId: z.string().max(64).optional().or(z.literal('')),
  note: optionalText(300),
  /** Products of the sale (optional); 推廣活動 product rules match their SKUs. */
  lines: z.array(saleLineSchema).max(50).optional(),
});

/** Native brands: manual change of points and/or stamps (a reason is required). */
export const adjustSchema = z
  .object({
    points: z.number().min(-10_000_000).max(10_000_000),
    stamps: z.number().int().min(-10_000).max(10_000),
    note: z.string().trim().min(1).max(300),
  })
  .refine((v) => v.points !== 0 || v.stamps !== 0, { message: 'change points or stamps', path: ['points'] });

export const issueCouponSchema = z.object({ rewardId: z.string().min(1).max(64) });
export const couponCodeSchema = z.object({ code: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{4,20}$/) });
export const couponStatusSchema = z.object({ status: z.enum(['redeemed', 'fulfilled', 'expired', 'cancelled']) });
export const switchBrandSchema = z.object({ brandId: z.string().min(1).max(64) });
export const portalHandoffSchema = z.object({ token: z.string().min(20).max(200) });

export const referralLinkSchema = z
  .object({
    code: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
    name: z.string().trim().min(1).max(80),
    source: z.string().trim().max(60).optional(),
    description: z.string().trim().max(500).optional(),
    /** Sign-ups count for the link only from validFrom and before validTo (null / absent = no limit). */
    validFrom: z.string().datetime({ offset: true }).nullable().optional(),
    validTo: z.string().datetime({ offset: true }).nullable().optional(),
    active: z.boolean(),
  })
  .refine((v) => !v.validFrom || !v.validTo || Date.parse(v.validTo) > Date.parse(v.validFrom), { message: 'must be after the start', path: ['validTo'] });

/** A visitor opened the member site with ?ref=<code>. `referrer` = host of the page they came from. */
export const refClickSchema = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
  referrer: z.string().trim().max(200).regex(/^[A-Za-z0-9.-]*$/).optional(),
});

/** The coupon's nature; older clients only send `redeemable`. */
export const rewardNature = (v: { nature?: 'redeem' | 'issue' | 'both'; redeemable: boolean }) => v.nature ?? (v.redeemable ? 'both' : 'issue');

/**
 * 優惠券. PROAN brands must pick the POS coupon (cash / gift); native brands leave posCouponId null
 * and may set how long the coupon lasts, a coupon number prefix and a cash coupon's value. A coupon
 * exchangeable on the member site (nature redeem / both) needs points > 0. totalLimit is the
 * starting stock of a new coupon; later changes go through the stock adjustment.
 */
export const rewardSchema = z
  .object({
    couponType: z.enum(['cash', 'gift', 'discount']),
    posCouponId: z.number().int().positive().nullable(),
    title: localizedTextSchema,
    description: localizedTextSchema,
    terms: localizedTextSchema,
    imageKey: z.string().max(300).nullable(),
    points: z.number().int().min(0).max(10_000_000),
    redeemable: z.boolean().default(true),
    nature: z.enum(['redeem', 'issue', 'both']).optional(),
    codePrefix: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{1,8}$/).nullable().default(null),
    discountMode: z.enum(['percent', 'amount']).nullable().default(null),
    discountValue: z.number().positive().max(1_000_000).nullable().default(null),
    tierIds: z.array(z.number().int()).min(1).max(50).nullable().default(null),
    expiryMode: z.enum(['none', 'fixed', 'days']).default('none'),
    expiryDate: isoDate.nullable().default(null),
    expiryDays: z.number().int().min(1).max(3650).nullable().default(null),
    redeemStart: z.string().datetime({ offset: true }).nullable(),
    redeemEnd: z.string().datetime({ offset: true }).nullable(),
    totalLimit: z.number().int().positive().nullable().default(null),
    perMemberLimit: z.number().int().positive().nullable(),
    /** POS 連接: 適用菜式編號 (兌換菜式 / 項目折扣); empty = the whole bill. */
    posItemCodes: posItemCodes.default(null),
    active: z.boolean(),
    sort: z.number().int().min(0).max(100000),
  })
  .refine((v) => rewardNature(v) === 'issue' || v.points > 0, { message: 'points needed to exchange', path: ['points'] })
  .refine((v) => v.expiryMode !== 'fixed' || !!v.expiryDate, { message: 'required', path: ['expiryDate'] })
  .refine((v) => v.expiryMode !== 'days' || !!v.expiryDays, { message: 'required', path: ['expiryDays'] })
  .refine((v) => (v.discountMode === null) === (v.discountValue === null), { message: 'required', path: ['discountValue'] })
  .refine((v) => v.discountMode !== 'percent' || (v.discountValue ?? 0) <= 100, { message: 'at most 100', path: ['discountValue'] });
export type RewardInput = z.infer<typeof rewardSchema>;

/** 調整庫存: add (+) or take away (−) coupons; change null = no limit. */
export const stockAdjustSchema = z
  .object({ change: z.number().int().min(-1_000_000).max(1_000_000).nullable(), note: z.string().trim().max(200).optional().or(z.literal('')) })
  .refine((v) => v.change !== 0, { message: 'change the stock', path: ['change'] });

export const decisionSchema = z.object({ note: optionalText(500) });

export const staffInviteSchema = z.object({
  email,
  name: z.string().trim().min(1).max(80),
  role: z.enum(['brand_owner', 'brand_staff']),
  /** Shops this person works at; null / absent = every shop. */
  shopIds: z.array(z.string().min(1).max(64)).max(50).nullable().optional(),
});

// ---------------------------------------------------------------- admin portal

export const proanConfigSchema = z.object({
  apiHost: z.string().trim().url().max(300),
  hqApiHost: z.string().trim().url().max(300).optional().or(z.literal('')),
  /** Write-only. Empty string keeps the stored key. */
  desKey: z.string().max(64).optional(),
  /** Write-only, 7 digits. Empty string keeps the stored code. */
  crmEncryptCode: z.string().regex(/^\d{7}$/).optional().or(z.literal('')),
  shopId: z.number().int().nullable(),
  stationId: z.number().int().nullable(),
});
export type ProanConfigInput = z.infer<typeof proanConfigSchema>;

const brandBase = {
  name: z.string().trim().min(1).max(120),
  contractStart: isoDate.nullable(),
  contractEnd: isoDate.nullable(),
  contactName: z.string().trim().min(1).max(80),
  contactEmail: email,
  contactTel: z.string().trim().max(40).nullable(),
  internalRemarks: z.string().trim().max(5000).nullable(),
  logoKey: z.string().max(300).nullable(),
  qrMode: z.enum(['fixed', 'dynamic']),
};

/**
 * Step 3 "Member data source": native = new database in this system (no PROAN settings, fixed QR);
 * proan = connect to the PROAN POS (PROAN settings required). Cannot be changed after creation.
 */
export const brandCreateSchema = z
  .object({
    ...brandBase,
    slug,
    template: z.enum(SITE_TEMPLATES),
    languages: z.array(langSchema).min(1),
    defaultLang: langSchema,
    ownerEmail: email,
    ownerName: z.string().trim().min(1).max(80),
    dataSource: z.enum(['proan', 'native']).default('proan'),
    proan: proanConfigSchema.extend({ desKey: z.string().min(1).max(64) }).nullable().default(null),
    customDomain: hostname.optional().or(z.literal('')),
  })
  .refine((v) => v.dataSource === 'native' || v.proan !== null, { message: 'required for PROAN brands', path: ['proan'] })
  .refine((v) => v.dataSource === 'proan' || v.qrMode === 'fixed', { message: 'new-database brands use a fixed QR', path: ['qrMode'] });
export type BrandCreateInput = z.infer<typeof brandCreateSchema>;

export const brandUpdateSchema = z.object(brandBase);
export type BrandUpdateInput = z.infer<typeof brandUpdateSchema>;

export const brandStatusSchema = z.object({ status: z.enum(['active', 'suspended']), reason: optionalText(500) });
/** 狀態及合約記錄: a note added by hand (e.g. contract renewed, payment received). */
export const brandNoteSchema = z.object({ note: z.string().trim().min(1).max(1000) });
export const domainAddSchema = z.object({ hostname });
export const adminInviteSchema = z.object({ email, name: z.string().trim().min(1).max(80), role: z.enum(ADMIN_ROLES).default('super') });
/** Admin sets a user's password directly; their other sessions end. */
export const adminSetPasswordSchema = z.object({ password: passwordSchema.min(10) });
